import React from 'react'

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: April 30, 2026</p>
        </div>

        {/* Introduction */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Introduction</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Star Nutrition ("we," "us," "our," or "Company") operates the Star Nutrition mobile and web applications. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our services.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Information We Collect</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Personal Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Phone number</li>
                <li>Name and email address</li>
                <li>Delivery addresses</li>
                <li>Payment information (processed securely)</li>
                <li>Order history and preferences</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Device & Location Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Device type, OS, and app version</li>
                <li>GPS location data (for delivery tracking)</li>
                <li>Camera access (for delivery photos and verification)</li>
                <li>Device identifiers and push notification tokens</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Usage Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>App interaction logs and feature usage</li>
                <li>Search queries and browsing patterns</li>
                <li>Pages visited and time spent</li>
                <li>Crash reports and performance metrics</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Use Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. How We Use Your Information</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li><strong>Order Processing:</strong> To process and fulfill your orders for nutritional products</li>
            <li><strong>Delivery:</strong> To track deliveries and enable delivery partners to locate you</li>
            <li><strong>Notifications:</strong> To send order updates, promotional offers, and important notifications</li>
            <li><strong>Customer Support:</strong> To respond to your inquiries and resolve issues</li>
            <li><strong>Payment Processing:</strong> To process secure payments for your orders</li>
            <li><strong>Analytics & Improvement:</strong> To understand usage patterns and improve our services</li>
            <li><strong>Fraud Prevention:</strong> To detect and prevent fraudulent transactions</li>
            <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
          </ul>
        </section>

        {/* Information Sharing */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Sharing of Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">We may share your information with:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li><strong>Delivery Partners:</strong> Your address and contact details for delivery purposes</li>
            <li><strong>Payment Processors:</strong> Necessary payment and transaction information</li>
            <li><strong>Service Providers:</strong> Third parties who assist us in operating our services</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            We do not sell your personal information to third parties for marketing purposes.
          </p>
        </section>

        {/* Data Security */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Data Security</h2>
          <p className="text-gray-700 leading-relaxed">
            We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. This includes:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mt-4">
            <li>Encrypted data transmission (HTTPS)</li>
            <li>Secure password authentication</li>
            <li>Regular security audits and updates</li>
            <li>Limited access to personal information</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        {/* Your Rights */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Your Rights</h2>
          <p className="text-gray-700 leading-relaxed mb-4">You have the right to:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>Access your personal information</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data (right to be forgotten)</li>
            <li>Opt-out of marketing communications</li>
            <li>Withdraw consent for data processing</li>
            <li>Export your data in a portable format</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            To exercise these rights, please contact us using the information below.
          </p>
        </section>

        {/* Cookies & Tracking */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Cookies & Tracking</h2>
          <p className="text-gray-700 leading-relaxed">
            Our web and mobile applications may use cookies, local storage, and similar tracking technologies to enhance user experience, remember preferences, and analyze usage patterns. You can control cookie settings through your browser preferences.
          </p>
        </section>

        {/* Third-Party Links */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Third-Party Links</h2>
          <p className="text-gray-700 leading-relaxed">
            Our application may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Children's Privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware of such collection, we will delete the information promptly.
          </p>
        </section>

        {/* Permission Usage */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. App Permissions</h2>
          <p className="text-gray-700 leading-relaxed mb-4">Our applications request the following permissions:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li><strong>Camera:</strong> For delivery verification photos and order documentation</li>
            <li><strong>Location:</strong> For accurate delivery tracking and mapping</li>
            <li><strong>Notifications:</strong> For order updates and important alerts</li>
            <li><strong>Phone Contacts:</strong> To enable quick customer support calls</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            You can grant or deny these permissions at any time through your device settings.
          </p>
        </section>

        {/* Changes to Policy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Changes to This Privacy Policy</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time. Changes will be effective immediately upon posting to our application. Your continued use of our services constitutes acceptance of the updated policy. We encourage you to review this policy periodically.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Contact Us</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            If you have questions about this Privacy Policy or our privacy practices, please contact us:
          </p>
          <div className="bg-gray-100 p-6 rounded-lg space-y-2">
            <p className="text-gray-700"><strong>Company:</strong> Star Nutrition</p>
            <p className="text-gray-700"><strong>Email:</strong> support@starnutritionsupplements.com</p>
            <p className="text-gray-700"><strong>Phone:</strong> +91 9876543210</p>
            <p className="text-gray-700"><strong>Address:</strong> Star Nutrition, India</p>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-600 text-sm text-center">
            Thank you for using Star Nutrition. We are committed to protecting your privacy and providing an excellent user experience.
          </p>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy